import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from "chart.js";
import { useQuery } from "@tanstack/react-query";
import { Bar } from "react-chartjs-2";

import { ChartPanel, DashboardsPageFrame, KpiCard, KpiGrid } from "./dashboardKit";
import { websiteTrafficSnapshotQueryOptions } from "./websiteTrafficQueries";
import type {
  WebsiteTrafficPeriod,
  WebsiteTrafficSnapshotResponse,
  WebsiteTrafficSource,
  WebsiteTrafficTopPage,
  WebsiteTrafficTrendPoint,
} from "./websiteTrafficTypes";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
);

const ORANGE = "#F7941D";
const BLUE = "#0081AF";
const SKY = "#00ABE7";
const CHANNEL_COLORS = [BLUE, SKY, ORANGE, "#A9A39C", "#D2CBCB", "#EAD2AC"];

function fmtInt(n: number): string {
  return Math.round(n).toLocaleString();
}

function fmtPct(n: number): string {
  return `${Number(n).toFixed(1)}%`;
}

function fmtDelta(d: number | null | undefined): string | null {
  if (d === null || d === undefined) return null;
  const sign = d >= 0 ? "+" : "";
  return `${sign}${d.toFixed(1)}% vs prior period`;
}

function deltaDirection(
  d: number | null | undefined,
  lowerIsBetter = false,
): "up" | "down" | undefined {
  if (d === null || d === undefined) return undefined;
  const good = lowerIsBetter ? d <= 0 : d >= 0;
  return good ? "up" : "down";
}

function pct(n: number, d: number): number {
  return d === 0 ? 0 : (100 * n) / d;
}

function formatAsOf(asOf: string): string {
  return new Date(`${asOf}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function trendLabel(dateYYYYMMDD: string): string {
  const s = String(dateYYYYMMDD);
  if (s.length !== 8) return s;
  return `${Number.parseInt(s.slice(4, 6), 10)}/${Number.parseInt(s.slice(6, 8), 10)}`;
}

function PeriodKpis({ title, period }: { title: string; period: WebsiteTrafficPeriod }) {
  return (
    <section className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      <KpiGrid>
        <KpiCard
          label="Sessions"
          value={fmtInt(period.sessions)}
          delta={fmtDelta(period.deltas.sessions)}
          deltaDirection={deltaDirection(period.deltas.sessions)}
        />
        <KpiCard
          label="Pageviews"
          value={fmtInt(period.pageviews)}
          delta={fmtDelta(period.deltas.pageviews)}
          deltaDirection={deltaDirection(period.deltas.pageviews)}
        />
        <KpiCard
          label="Active Users"
          value={fmtInt(period.activeUsers)}
          delta={fmtDelta(period.deltas.activeUsers)}
          deltaDirection={deltaDirection(period.deltas.activeUsers)}
        />
        <KpiCard
          label="Bounce Rate"
          value={fmtPct(period.bounceRate * 100)}
          delta={fmtDelta(period.deltas.bounceRate)}
          deltaDirection={deltaDirection(period.deltas.bounceRate, true)}
        />
      </KpiGrid>
    </section>
  );
}

function SessionsTrendChart({ trend }: { trend: WebsiteTrafficTrendPoint[] }) {
  const labels = trend.map((row) => trendLabel(row.date));
  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: {
        grid: { color: "rgba(148, 163, 184, 0.25)" },
        ticks: { color: "#64748b", font: { size: 11 } },
      },
      x: {
        grid: { display: false },
        ticks: { color: "#64748b", font: { size: 10 } },
      },
    },
  };

  const data = {
    labels,
    datasets: [
      {
        label: "Sessions",
        data: trend.map((row) => row.sessions),
        backgroundColor: BLUE,
        borderRadius: 4,
        yAxisID: "y",
      },
      {
        type: "line" as const,
        label: "Active Users",
        data: trend.map((row) => row.activeUsers),
        borderColor: ORANGE,
        backgroundColor: "transparent",
        pointBackgroundColor: ORANGE,
        pointRadius: 3,
        borderWidth: 2,
        yAxisID: "y",
      },
    ],
  } as ChartData<"bar">;

  return (
    <ChartPanel title="Sessions — last 7 days">
      <div className="relative h-[200px]">
        <Bar data={data} options={options} />
      </div>
      <div className="mt-2 flex gap-4 text-xs font-semibold">
        <span style={{ color: BLUE }}>■ sessions</span>
        <span style={{ color: ORANGE }}>— active users</span>
      </div>
    </ChartPanel>
  );
}

function TrafficSourcesPanel({ sources }: { sources: WebsiteTrafficSource[] }) {
  const totalSessions = sources.reduce((sum, row) => sum + row.sessions, 0);

  return (
    <ChartPanel title="Traffic sources (last 7 days)">
      <div className="flex flex-col gap-2.5">
        {sources.map(({ channel, sessions }, index) => {
          const share = pct(sessions, totalSessions);
          return (
            <div key={channel} className="flex items-center gap-2">
              <div className="w-[110px] shrink-0 text-right text-xs text-foreground">{channel}</div>
              <div className="h-3.5 flex-1 overflow-hidden rounded-sm bg-muted">
                <div
                  className="h-full rounded-sm"
                  style={{
                    width: `${share}%`,
                    background: CHANNEL_COLORS[index] || "#C9C5BE",
                  }}
                />
              </div>
              <div className="w-[70px] text-right text-xs text-muted-foreground">
                {fmtInt(sessions)}{" "}
                <span className="text-muted-foreground/70">({Math.round(share)}%)</span>
              </div>
            </div>
          );
        })}
        {!sources.length ? (
          <p className="text-sm text-muted-foreground">No traffic sources for this window.</p>
        ) : null}
      </div>
    </ChartPanel>
  );
}

function TopPagesPanel({ topPages }: { topPages: WebsiteTrafficTopPage[] }) {
  const maxViews = topPages[0]?.pageviews || 1;

  return (
    <ChartPanel title={`Top ${topPages.length} pages (last 7 days)`}>
      <div className="flex flex-col gap-2">
        {topPages.map(({ path, pageviews }, index) => (
          <div key={path} className="flex items-center gap-3">
            <div className="w-[18px] shrink-0 text-right text-xs text-muted-foreground">
              {index + 1}
            </div>
            <div className="h-3.5 flex-1 overflow-hidden rounded-sm bg-muted">
              <div
                className="h-full rounded-sm bg-muted-foreground/40"
                style={{ width: `${pct(pageviews, maxViews)}%` }}
              />
            </div>
            <div className="w-[200px] truncate font-mono text-xs text-foreground" title={path}>
              {path}
            </div>
            <div className="w-[70px] text-right text-xs text-muted-foreground">
              {fmtInt(pageviews)} views
            </div>
          </div>
        ))}
        {!topPages.length ? (
          <p className="text-sm text-muted-foreground">No pageviews for this window.</p>
        ) : null}
      </div>
    </ChartPanel>
  );
}

function DashboardBody({ data }: { data: WebsiteTrafficSnapshotResponse }) {
  return (
    <>
      <PeriodKpis title="Yesterday" period={data.yesterday} />
      <PeriodKpis title="Last 7 days" period={data.week} />
      <div className="grid gap-3 lg:grid-cols-[1fr_320px]">
        <SessionsTrendChart trend={data.trend} />
        <TrafficSourcesPanel sources={data.trafficSources} />
      </div>
      <TopPagesPanel topPages={data.topPages} />
    </>
  );
}

export function WebsiteTrafficDashboard() {
  const query = useQuery(websiteTrafficSnapshotQueryOptions());

  return (
    <DashboardsPageFrame className="flex flex-col gap-4">
      <header>
        <h1 className="text-xl font-bold tracking-tight text-foreground">Website Traffic</h1>
        {query.data ? (
          <p className="mt-1 text-sm text-muted-foreground">
            As of {formatAsOf(query.data.asOf)} · yesterday vs same day last week · 7-day window vs
            prior 7 days
          </p>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">
            GA4 sessions, pageviews, active users, bounce rate, top pages, and traffic sources.
          </p>
        )}
      </header>

      {query.isPending ? (
        <div className="rounded-lg border border-border/70 bg-background/80 px-4 py-8 text-sm text-muted-foreground">
          Loading Website Traffic…
        </div>
      ) : null}

      {query.isError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-8 text-sm text-destructive">
          {query.error instanceof Error
            ? query.error.message
            : "Website Traffic data is unavailable. Please try again."}
        </div>
      ) : null}

      {query.data ? <DashboardBody data={query.data} /> : null}
    </DashboardsPageFrame>
  );
}
